import { FC, ReactNode, useMemo } from 'react';
import { Avatar, Col } from 'antd';
import { TbCubeSpark, TbDatabase } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { SmallTag } from '@/shared/components/tags';
import { useConnectedAccountStore, useReportComponentStore } from '@/stores';
import { ConnectedAccount, ConnectedAccountType, ReportComponent } from '@/types';
import { DataSourceType } from '@/types/api/modules/business-intelligence';

interface ReportComponentDataSourceTagProps {
    component: ReportComponent;
}

interface DataSourceTagModel {
    label: string;
    icon: ReactNode;
    src?: string;
}

const safeDecodeURIComponent = (value?: string) => {
    try {
        return decodeURIComponent(value ?? '');
    } catch {
        return value ?? '';
    }
};

const parseConnectedAccountProviderId = (datasetKey?: string) => {
    const [prefix, providerId] = String(datasetKey ?? '')
        .split(':')
        .map(safeDecodeURIComponent);

    if (prefix !== 'connected-account' || !providerId) {
        return undefined;
    }

    return providerId;
};

const parseVantageDataBlockDatasetId = (datasetKey?: string) => {
    const [prefix, componentId, dataBlockId, ...rest] = String(datasetKey ?? '')
        .split(':')
        .map(safeDecodeURIComponent);

    if (prefix !== 'vantage-datablock' || !componentId || !dataBlockId || rest.length) {
        return undefined;
    }

    return { componentId, dataBlockId };
};

const getConnectedAccountLabel = (connectedAccount?: ConnectedAccount) =>
    connectedAccount?.nickname || connectedAccount?.type || 'Custom Source';

const ReportComponentDataSourceTag: FC<ReportComponentDataSourceTagProps> = ({ component }) => {
    const { token } = useAntDesignTheme();
    const { integrationLabelToIconMap } = useMaps();
    const connectedAccounts = useConnectedAccountStore((state) => state.connectedAccounts);
    const reportComponents = useReportComponentStore((state) => state.reportComponents);
    const reportComponentsById = useReportComponentStore((state) => state.reportComponentsById);

    const dataSourceTag = useMemo<DataSourceTagModel | undefined>(() => {
        const dataSet = component.configuration?.dataSet;

        if (dataSet?.type !== DataSourceType.THIRD_PARTY) {
            return undefined;
        }

        const providerId = dataSet.providerId ?? parseConnectedAccountProviderId(dataSet.key);
        const connectedAccount =
            connectedAccounts.find((account) => account.id === providerId) ??
            (component.dataSource?.id === providerId ? component.dataSource : undefined);

        if (providerId || connectedAccount) {
            const connectedAccountType = connectedAccount?.type;

            return {
                label: getConnectedAccountLabel(connectedAccount),
                src: connectedAccountType
                    ? integrationLabelToIconMap[connectedAccountType as ConnectedAccountType]
                    : undefined,
                icon: <TbDatabase size='0.7rem' style={{ color: token.colorTextTertiary }} />,
            };
        }

        const parsedDataBlock = parseVantageDataBlockDatasetId(dataSet.key);

        if (parsedDataBlock) {
            const sourceComponent =
                reportComponentsById[parsedDataBlock.componentId] ??
                reportComponents.find((reportComponent) => reportComponent.id === parsedDataBlock.componentId);
            const sourceDataBlock = (sourceComponent?.configuration as any)?.dataBlocks?.find(
                (dataBlock: any) => dataBlock.id === parsedDataBlock.dataBlockId,
            );

            return {
                label: sourceDataBlock?.referenceName ?? 'Datablock',
                icon: <TbCubeSpark size='0.7rem' style={{ color: token.colorTextTertiary }} />,
            };
        }

        return {
            label: 'Custom Source',
            icon: <TbDatabase size='0.7rem' style={{ color: token.colorTextTertiary }} />,
        };
    }, [
        component.configuration,
        component.dataSource,
        connectedAccounts,
        integrationLabelToIconMap,
        reportComponents,
        reportComponentsById,
        token.colorTextTertiary,
    ]);

    if (!dataSourceTag) {
        return null;
    }

    return (
        <Col flex='none' style={{ minWidth: 0, maxWidth: '45%' }}>
            <SmallTag
                bordered
                padding='0.0625rem 0.25rem'
                styles={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    maxWidth: '100%',
                    marginLeft: 0,
                    color: token.colorTextSecondary,
                    backgroundColor: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    verticalAlign: 0,
                }}
                icon={
                    <Avatar
                        size={16}
                        shape='square'
                        src={dataSourceTag.src}
                        icon={dataSourceTag.src ? undefined : dataSourceTag.icon}
                        style={{
                            flex: '0 0 auto',
                            backgroundColor: token.colorBgBase,
                            border: `1px solid ${token.colorBorder}`,
                        }}
                    />
                }
                message={
                    <span
                        style={{
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {dataSourceTag.label}
                    </span>
                }
            />
        </Col>
    );
};

export default ReportComponentDataSourceTag;
